import { MICROS_PER_UNIT, MILLITOKENS_PER_TOKEN } from '@traceio-org/platform-contracts';

/**
 * Units of the billing contract, formatted for people and parsed from what they type. Money arrives as an integer
 * number of micros of the currency and tokens as integer millitokens; nothing here prices anything: it only changes
 * units (micros to dollars, millitokens to tokens) with exact integer arithmetic. Prices are what platform-api
 * sent, never computed in the browser.
 */

const MICROS_PER_CENT = MICROS_PER_UNIT / 100;
const groupFormat = new Intl.NumberFormat(undefined, { useGrouping: true, maximumFractionDigits: 0 });

/** Micros to cents, half to even, as platform-api and the provider round (1,234,500 micros: 123 cents, half to even). */
export function microsToCents(micros: number): number {
  const sign = micros < 0 ? -1 : 1;
  const absolute = Math.abs(micros);
  const whole = Math.floor(absolute / MICROS_PER_CENT);
  const remainder = absolute % MICROS_PER_CENT;
  const half = MICROS_PER_CENT / 2;
  let cents = whole;
  if (remainder > half || (remainder === half && whole % 2 === 1)) cents += 1;
  return sign * cents;
}

/** An amount for people, to the cent: 1,500.00 USD (negative amounts keep their sign: -50.00 USD). */
export function formatMoney(micros: number, currency: string): string {
  const cents = microsToCents(micros);
  const sign = cents < 0 ? '-' : '';
  const absolute = Math.abs(cents);
  return `${sign}${groupFormat.format(Math.floor(absolute / 100))}.${String(absolute % 100).padStart(2, '0')} ${currency}`;
}

/**
 * A unit price exactly as the plan version holds it: at least two decimals, up to six (0.50 USD per token;
 * 0.123456 USD per token). Never rounded: a price below a cent is still a price.
 */
export function formatUnitPrice(micros: number, currency: string): string {
  const sign = micros < 0 ? '-' : '';
  const absolute = Math.abs(micros);
  const whole = Math.floor(absolute / MICROS_PER_UNIT);
  const fraction = String(absolute % MICROS_PER_UNIT)
    .padStart(6, '0')
    .replace(/0+$/, '')
    .padEnd(2, '0');
  return `${sign}${groupFormat.format(whole)}.${fraction} ${currency}`;
}

/** Tokens from millitokens, with the fraction only when there is one: 3,200 tokens; 5.1 tokens. */
export function formatTokens(millitokens: number): string {
  const sign = millitokens < 0 ? '-' : '';
  const absolute = Math.abs(millitokens);
  const whole = groupFormat.format(Math.floor(absolute / MILLITOKENS_PER_TOKEN));
  const fraction = String(absolute % MILLITOKENS_PER_TOKEN)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return fraction ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
}

/** A level multiplier in thousandths as a factor: 800 is 0.8, 1250 is 1.25, 1000 is 1. */
export function formatMultiplier(milli: number): string {
  const whole = Math.floor(milli / 1000);
  const fraction = String(milli % 1000)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : String(whole);
}

/** Basis points as a percentage: 1000 is 10%, 1250 is 12.5%. */
export function formatPercent(basisPoints: number): string {
  const whole = Math.floor(basisPoints / 100);
  const fraction = String(basisPoints % 100)
    .padStart(2, '0')
    .replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}%` : `${whole}%`;
}

/**
 * The amount a staff member typed, in units of the currency, as micros: digits with an optional group separator
 * and up to six decimals, the precision of micros (1,500 or 1500.50), so an existing cap below the cent reads back
 * unchanged. null when it is not an amount. The result is exact (no float).
 */
export function parseMoneyToMicros(text: string): number | null {
  const cleaned = text.replace(/[\s,]/g, '');
  const match = /^(\d+)(?:\.(\d{1,6}))?$/.exec(cleaned);
  if (!match) return null;
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? '').padEnd(6, '0'));
  const micros = whole * MICROS_PER_UNIT + fraction;
  return Number.isSafeInteger(micros) ? micros : null;
}

/**
 * Units of a currency from micros, exactly, as a field value (1500.5 for 1,500,500,000 micros; 1500.005 for
 * 1,500,005,000): never rounded, so saving a dialog without touching the field never changes the amount.
 */
export function microsToInput(micros: number): string {
  const absolute = Math.abs(micros);
  const whole = Math.floor(absolute / MICROS_PER_UNIT);
  const fraction = String(absolute % MICROS_PER_UNIT)
    .padStart(6, '0')
    .replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : String(whole);
}
